import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  IFormInput,
  PasswordChangeResponse,
  PasswordData,
  putChangePassword,
} from '~/features/ChangePassword/ChangePasswordForm/data';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { browserLogError } from '~/utils/helper';

type PropsType = {
  username: string;
  onUpdate: () => void;
};

export const useChangePasswordForm = (props: PropsType) => {
  const { username } = props;

  const { t } = useTranslation();

  const [submissionResponse, setSubmissionResponse] = useState<
    PasswordChangeResponse | undefined
  >(undefined);

  const validationSchema = React.useMemo(() => {
    return yup.object().shape<IFormInput>({
      currentPassword: yup.string().required(t('fieldIsRequired')),
      newPassword: yup
        .string()
        .required(t('fieldIsRequired'))
        .min(6, t('laValidPassword')),
      confirmPassword: yup
        .string()
        .required(t('fieldIsRequired'))
        .oneOf([yup.ref('newPassword')], t('laNewPassNotMatch')),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  const form = useForm<IFormInput>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    resolver,
  });
  const { handleSubmit } = form;

  const { enqueueSnackbar } = useSnackbar();

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      const json: PasswordData = {
        fLoginPassword: data.currentPassword,
        fLoginNewPassword: data.newPassword,
      };

      putChangePassword(json, username)
        .then((response) => {
          setSubmissionResponse(response);
          if (response.fLoginPwChangeSuccess) {
            enqueueSnackbar(t('successPasswordChanged'), {
              variant: 'success',
            });
          } else {
            enqueueSnackbar(t('laPasswordResetFail'), {
              variant: 'error',
            });
          }
        })
        .catch((err) => {
          browserLogError('putChangePassword error', err);
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        });
    });
  }, [enqueueSnackbar, handleSubmit, t, username]);

  const onSave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleFormSubmit();
  };

  return {
    form,
    onSave,
    submissionResponse,
  };
};
